import { DADOS_PADRAO, OPCOES_PADRAO, dadosIniciais } from './manual-defaults';
import { novoId } from './manual-utils';
import type { ManualData } from './manual-types';

export const VERSAO_FORMATO = 1;

export interface ArquivoManual {
  formato: 'riskguard.manual-operacao';
  versao: number;
  exportadoEm: string;
  dados: ManualData;
}

export function chaveDoRascunho(projetoId?: string | number | null): string {
  return `manual-operacao:${projetoId ?? 'rascunho'}`;
}

/** Garante que todo item de lista tenha `id` — arquivos antigos ou editados à mão podem não ter. */
function comIds(valor: unknown): unknown {
  if (Array.isArray(valor)) {
    return valor.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const objeto = { ...(item as Record<string, unknown>) };
      if (!objeto.id) objeto.id = novoId('imp');
      for (const [chave, conteudo] of Object.entries(objeto)) {
        if (Array.isArray(conteudo)) objeto[chave] = comIds(conteudo);
      }
      return objeto;
    });
  }
  return valor;
}

/** Mescla o conteúdo recebido sobre o padrão, preservando campos novos do modelo. */
export function normalizarManual(bruto: unknown): ManualData {
  const base = dadosIniciais();
  if (!bruto || typeof bruto !== 'object') return base;
  const entrada = bruto as Record<string, unknown>;
  const resultado: Record<string, unknown> = { ...base };

  for (const chave of Object.keys(DADOS_PADRAO) as (keyof ManualData)[]) {
    if (chave === 'opcoes') continue;
    const valor = entrada[chave];
    if (valor === undefined || valor === null) continue;
    const padrao = DADOS_PADRAO[chave];
    if (Array.isArray(padrao)) {
      resultado[chave] = Array.isArray(valor) ? comIds(valor) : padrao;
    } else {
      resultado[chave] = valor;
    }
  }

  const opcoes = entrada.opcoes && typeof entrada.opcoes === 'object' ? entrada.opcoes : {};
  resultado.opcoes = { ...OPCOES_PADRAO, ...(opcoes as Record<string, unknown>) };
  return resultado as unknown as ManualData;
}

export function carregarRascunho(chave: string): ManualData | null {
  try {
    const bruto = localStorage.getItem(chave);
    if (!bruto) return null;
    const conteudo = JSON.parse(bruto);
    const dados = conteudo && typeof conteudo === 'object' && 'dados' in conteudo ? conteudo.dados : conteudo;
    return normalizarManual(dados);
  } catch {
    return null;
  }
}

export function salvarRascunho(chave: string, dados: ManualData): boolean {
  try {
    const pacote: ArquivoManual = {
      formato: 'riskguard.manual-operacao',
      versao: VERSAO_FORMATO,
      exportadoEm: new Date().toISOString(),
      dados,
    };
    localStorage.setItem(chave, JSON.stringify(pacote));
    return true;
  } catch {
    // cota estourada (imagens grandes em base64) — o editor avisa o usuário
    return false;
  }
}

export function removerRascunho(chave: string) {
  try {
    localStorage.removeItem(chave);
  } catch {
    /* nada a fazer */
  }
}

export function serializarManual(dados: ManualData): string {
  const pacote: ArquivoManual = {
    formato: 'riskguard.manual-operacao',
    versao: VERSAO_FORMATO,
    exportadoEm: new Date().toISOString(),
    dados,
  };
  return JSON.stringify(pacote, null, 2);
}

export function desserializarManual(conteudo: string): ManualData {
  const bruto = JSON.parse(conteudo);
  const dados = bruto && typeof bruto === 'object' && 'dados' in bruto ? bruto.dados : bruto;
  return normalizarManual(dados);
}
